import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/solid";
import { useState } from "react";
import { ArtifactType, CoachingSourceNode } from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import AppLink from "@components/link/link";
import { classNames } from "@helpers/css";

import { sourceTypeDot } from "./coaching-constants";

export const SourcesSection = ({
  sources,
}: {
  sources: CoachingSourceNode[];
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const label = useLabel();

  const sourceTypeLabel: Record<string, string> = {
    meeting: label("oneonone", { capitalize: true }),
    goal: label(ArtifactType.Goal, { capitalize: true }),
    recognition: label(ArtifactType.Recognition, { capitalize: true }),
    checkin: "Check-in",
    work_summary: "Work Summary",
  };

  const grouped = sources.reduce<Record<string, CoachingSourceNode[]>>(
    (acc, s) => {
      const key = s.type ?? "unknown";
      (acc[key] ||= []).push(s);
      return acc;
    },
    {},
  );

  return (
    <div className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
      <button
        type="button"
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
          )}
          <h3 className="text-base font-semibold text-gray-900">Sources</h3>
          <span className="text-sm text-gray-400">({sources.length})</span>
        </div>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 flex flex-col gap-4">
          {Object.entries(grouped).map(([type, items]) => (
            <div key={type}>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                {sourceTypeLabel[type] || type} ({items.length})
              </h4>
              <ul className="flex flex-col gap-1">
                {items.map((source) => (
                  <li
                    key={source.sourceRef}
                    className="flex items-center gap-2 text-sm text-gray-600 py-1"
                  >
                    <span
                      className={classNames(
                        "h-2 w-2 rounded-full shrink-0",
                        sourceTypeDot[source.type ?? ""] || "bg-gray-300",
                      )}
                    />
                    {(() => {
                      const url = source.sourceUrl;
                      return url ? (
                        <AppLink
                          to={url}
                          className="hover:underline text-blue-600"
                        >
                          {source.title}
                        </AppLink>
                      ) : (
                        <span>{source.title}</span>
                      );
                    })()}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
